import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TrendingUp, Users, Wrench } from 'lucide-react';

export default function TechnicianKPI() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [techs, setTechs] = useState([]);
  const [sps, setSps] = useState([]);

  const fetchAll = async () => {
    const q = new URLSearchParams();
    if (from) q.append('date_from', from);
    if (to) q.append('date_to', to);
    const [t, s] = await Promise.all([
      axios.get(`${API}/work-orders/kpi/technicians?${q.toString()}`, { withCredentials: true }),
      axios.get(`${API}/work-orders/kpi/service-providers?${q.toString()}`, { withCredentials: true }),
    ]);
    setTechs(t.data || []);
    setSps(s.data || []);
  };

  useEffect(() => { fetchAll(); }, [from, to]);

  const gradeColor = (g) => ({ A: 'bg-emerald-100 text-emerald-800', B: 'bg-sky-100 text-sky-800', C: 'bg-amber-100 text-amber-800', D: 'bg-orange-100 text-orange-800', F: 'bg-rose-100 text-rose-800' }[g] || '');

  return (
    <Layout>
      <div data-testid="technician-kpi-page" className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><TrendingUp className="w-7 h-7 text-indigo-500" />Technician & Provider KPI</h1>
          <p className="text-sm text-muted-foreground">Composite scorecards from Work Order execution data.</p>
        </div>
        <Card className="p-3 flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">From</Label><Input type="date" className="h-9 w-[150px]" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><Label className="text-xs">To</Label><Input type="date" className="h-9 w-[150px]" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        </Card>

        <Card className="p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b flex items-center gap-2"><Users className="w-4 h-4 text-violet-500" /><h2 className="font-semibold">In-house Technicians</h2></div>
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-xs uppercase text-slate-600">
              <tr><th className="px-3 py-2 text-left">Technician</th><th>Assigned</th><th>Completed</th><th>Completion %</th><th>SLA Hit %</th><th>Hours</th><th>Cost</th></tr>
            </thead>
            <tbody>
              {techs.length === 0 && (<tr><td colSpan={7} className="text-center text-muted-foreground py-8">No Work Orders found for the selected period.</td></tr>)}
              {techs.map((r, i) => (
                <tr key={r.user_id} data-testid={`tech-row-${i}`} className="border-t">
                  <td className="px-3 py-2 font-medium">{r.user_name}</td>
                  <td className="px-3 py-2 text-center">{r.assigned}</td>
                  <td className="px-3 py-2 text-center">{r.completed}</td>
                  <td className="px-3 py-2 text-center">{r.completion_rate}%</td>
                  <td className="px-3 py-2 text-center">{r.sla_hit_rate}%</td>
                  <td className="px-3 py-2 text-center">{r.total_hours?.toFixed?.(1) ?? 0}</td>
                  <td className="px-3 py-2 text-center">{r.total_cost?.toFixed?.(2) ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card className="p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b flex items-center gap-2"><Wrench className="w-4 h-4 text-amber-500" /><h2 className="font-semibold">Service Providers</h2></div>
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-xs uppercase text-slate-600">
              <tr><th className="px-3 py-2 text-left">Provider</th><th>Code</th><th>Assigned</th><th>Completed</th><th>Completion %</th><th>SLA Hit %</th><th>Score</th><th>Grade</th></tr>
            </thead>
            <tbody>
              {sps.length === 0 && (<tr><td colSpan={8} className="text-center text-muted-foreground py-8">No service-provider WOs in the selected period.</td></tr>)}
              {sps.map((r, i) => (
                <tr key={r.service_provider_id} data-testid={`sp-row-${i}`} className="border-t">
                  <td className="px-3 py-2 font-medium">{r.service_provider_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.service_provider_code || '—'}</td>
                  <td className="px-3 py-2 text-center">{r.assigned}</td>
                  <td className="px-3 py-2 text-center">{r.completed}</td>
                  <td className="px-3 py-2 text-center">{r.completion_rate}%</td>
                  <td className="px-3 py-2 text-center">{r.sla_hit_rate}%</td>
                  <td className="px-3 py-2 text-center font-semibold">{r.score}</td>
                  <td className="px-3 py-2 text-center"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${gradeColor(r.grade)}`}>{r.grade}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </Layout>
  );
}
